<template>
  <div>
    <v-card
      v-if="quoteItems"
      :class="breakPointChange"
      class="ml-auto px-5 pt-5 pb-5 mt-6 priceCard body-2"
      elevation="0"
    >

    <div v-if="!isEviction">
      <!-- subtotal -->
      <v-row v-if="hasSubtotal" no-gutter>
        <v-col class="px-3 pt-0">
          {{ $t('quote.priceTable.subtotal') }}
        </v-col>
        <v-col class="text-right px-3 pt-0">
          {{ subtotal }}
        </v-col>
      </v-row>
      <!-- subtotal -->

      <!-- pix tax -->
      <v-row v-if="hasVendorFee" no-gutter>
        <v-col class="px-3 pt-0">
          {{ $t('quote.priceTable.pixTax') }}
        </v-col>
        <v-col class="text-right px-3 pt-0">
          {{ pixTax }}
        </v-col>
      </v-row>
      <!-- pix tax -->

      <!-- vendor discount -->
      <v-row v-if="hasVendorDiscount" no-gutter>
        <v-col class="px-3 pt-0">
          {{ $t('quote.priceTable.vendorDiscount') }}
        </v-col>
        <v-col class="text-right px-3 pt-0">
          {{ vendorDiscount }}
        </v-col>
      </v-row>
      <!-- vendor discount -->

      <!-- FCI -->
      <v-row :class="{ 'd-print-none': simplifiedPrinting }" v-if="hasFCI && !payWithFci" no-gutter>
        <v-col class="px-3 pt-0">
          {{ $t('quote.priceTable.usedFci') }}
        </v-col>
        <v-col class="text-right px-3 pt-0">
          {{ formatMoney(quoteItems.net_fci) }}
        </v-col>
      </v-row>

      <v-row :class="{ 'd-print-none': simplifiedPrinting }" v-if="payWithFci && cashDiscountIsBiggerThanZero" no-gutter>
        <v-col class="px-3 pt-0">
          {{ $t('quote.priceTable.cashDiscount') }}
        </v-col>
        <v-col class="text-right px-3 pt-0">
          {{ formatMoney(quoteItems.cash_discount) }}
        </v-col>
      </v-row>
      <!-- /FCI -->
    </div>

      <!-- total -->
      <QuotePricesTotalV2
        :quotes="quotes"
        :quoteItems="quoteItems"
        :isEviction="isEviction"
        :payWithFci="payWithFci"
        :simplifiedPrinting="simplifiedPrinting"
      />
      <!-- total -->

      <p v-if="!payWithFci && !isEviction && countCardInstallments > 1" class="mb-0 text-center">
        <v-btn @click="openModal('INSTALLMENTS_MODAL')" small text>
          <span class="caption primary--text">
            {{ $t('quote.modal.seeAvailableInstallments') }}
          </span>
        </v-btn>
      </p>
    </v-card>

    <QuotePaymentMethodsV2
      v-if="isQuoteAlreadyPaid"
      :quotes="quotes"
      :quoteItems="quoteItems"
      :isEviction="isEviction"
      :payWithFci="payWithFci"
      :paymentMethod="paymentMethod"
    />

    <QuoteInstallmentsModal :quoteItems="quoteItems" />
  </div>
</template>

<script>
import QuotePaymentMethodsV2 from '@/views/quote/components/QuotePaymentMethodsV2'
import QuoteInstallmentsModal from './QuoteInstallmentsModal'
import QuotePricesTotalV2 from  './QuotePricesTotalV2'

import { mapMutations } from 'vuex'
import { catalogMonetary } from '@/util/catalog'

export default {
  name: 'QuotePrices',

  components: {
    QuoteInstallmentsModal,
    QuotePaymentMethodsV2,
    QuotePricesTotalV2,
  },

  props: {
    quotes: {
      type: Object,
    },

    quoteItems: {
      type: Object,
    },

    appointmentType: {
      type: String,
    },

    isQuoteAlreadyPaid: {
      type: Boolean,
      default: false,
    }
  },

  computed: {
    breakPointChange() {
      return !this.$vuetify.breakpoint.mobile && 'mw-400'
    },

    isEviction() {
      return this.appointmentType === 'EVICTION'
    },

    paymentMethod() {
      return this.quoteItems?.charge?.installments[0]?.payment_method
    },

    hasTotal() {
      return this.quoteItems?.total > 0
    },

    hasSubtotal() {
      return this.quoteItems?.subtotal > 0
    },

    hasFCI() {
      return this.quoteItems?.fci > 0
    },

    hasTotalByInstallments() {
      return this.quoteItems?.total_by_installments > 0
    },

    hasVendorFee() {
      return this.pixPaymentData?.taxes
    },

    hasVendorDiscount() {
      return this.quoteItems?.vendor_discount > 0
    },

    payWithFci() {
      return this.quoteItems ? this.quoteItems?.total == 0 : false
    },

    countCardInstallments() {
      if (this.quoteItems?.payment_options?.length <= 0) return

      return this.quoteItems?.payment_options.reduce((accumulator, item) => {
        if (item.payment_method === 'ASAAS_CREDIT_CARD') return accumulator + 1
        else return accumulator
      }, 0)
    },

    // values
    total() {
      return this.quoteItems?.total && this.formatMoney(this.quoteItems?.total)
    },

    totalByInstallments() {
      return this.quoteItems?.total_by_installments && this.formatMoney(this.quoteItems?.total_by_installments)
    },

    subtotal() {
      return this.quoteItems?.subtotal && this.formatMoney(this.quoteItems?.subtotal)
    },

    subtotalByInstallments() {
      return this.quoteItems?.subtotal_by_installments && this.formatMoney(this.quoteItems?.subtotal_by_installments)
    },

    vendorFee() {
      return this.quoteItems?.vendor_service_charge_fee && this.formatMoney(this.quoteItems?.vendor_service_charge_fee)
    },

    pixPaymentData() {
      if (this.quoteItems?.payment_options.length <= 0) return
      return this.quoteItems?.payment_options.find(({ payment_method }) => payment_method === 'ASAAS_PIX')
    },

    pixTax() {
      if (!this.pixPaymentData?.taxes) return
      return this.formatMoney(this.pixPaymentData?.taxes)
    },

    vendorDiscount() {
      return this.quoteItems?.vendor_discount && this.formatMoney(this.quoteItems?.vendor_discount)
    },

    netFci() {
      return this.quoteItems.net_fci && this.formatMoney(this.quoteItems.net_fci)
    },
    // ------

    cashDiscountIsBiggerThanZero() {
      return this.quoteItems.cash_discount > 0
    }
  },

  methods: {
    ...mapMutations(['openModal']),
  },

  data() {
    return {
      formatMoney: catalogMonetary,
      simplifiedPrinting: false,
    }
  },

  mounted() {
    this.$root.$on('isSimplifiedPrinting', (value) => {
      this.simplifiedPrinting = value
    })
  }
}
</script>

<style scoped>
@media print {
  .priceCard {
    background-color: #e6f9fe;
    -webkit-print-color-adjust: exact;
  }
}

.priceCard {
  background-color: #e6f9fe;
}

.priceCard .body-2 {
  color: #21566e;
}

.priceCard .body-2 {
  opacity: 0.8;
}
</style>
